"use client";

import { Button } from "@hero-app/components/ui/button";
import { Soffi } from "@hero-app/components/ui/icons/soffi";
import { cn } from "@hero-app/lib/utils";

import { AgentsTable } from "./agents-table";
import { ReferralCard } from "./referral-card";
import { SectionHeading } from "./section-heading";
import { SidebarNav } from "./sidebar-nav";
import { StatCard } from "./stat-card";
import { TimeFilter } from "./time-filter";

export interface EditorDashboardContentProps {
  className?: string;
}

const STAT_CARDS = [
  { label: "Actions Available", value: "169", total: "of 200 total", trend: "33%", pos: true },
  { label: "Credits Available", value: "680", total: "of 1,000 total", trend: "12%", pos: false },
  { label: "Seats used", value: "5", total: "of 10 total", trend: null, pos: null },
] as const;

// Composition root for the editor dashboard. Each region is its own named
// component (in its own file) so the Soffi editor labels it distinctly — the
// runtime shows the nearest user-component name. Region roots stay
// position:static (no `relative`/absolute) so the runtime classifies them as
// drag-to-reorder-able — getComputedStyle(el).position must be "static".
export function EditorDashboardContent({ className }: EditorDashboardContentProps) {
  return (
    <div
      className={cn(
        "flex h-full min-h-0 w-full max-w-full flex-col gap-y-3 overflow-hidden p-1",
        className,
      )}
      style={{ background: "#FAFAFA" }}
    >
      {/* Band 1 — brand + page header. Flex row, top-aligned. Two columns nest
       *  as flex children so every region is a direct flex sibling → the editor
       *  runtime can drag-reorder them. */}
      <div className="flex items-start">
        <div className="w-[min(140px,28%)] min-w-[108px] shrink-0 px-2 pt-2">
          <h1 className="flex items-center gap-1.5 text-[14px] font-medium leading-none tracking-[-0.4px] text-[#1a2026]">
            <Soffi className="size-3.5 shrink-0" aria-hidden />
            Acme.inc
          </h1>
        </div>

        <div className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-2 px-2 pt-2">
          <SectionHeading
            as="h1"
            title="Imagine your app here"
            subtitle="Your product could be edited straight from design "
          />

          <div className="flex items-center gap-1.5">
            <TimeFilter />
            <Button variant="primary" icon="enabled" iconGlyph="plus">
              Create
            </Button>
          </div>
        </div>
      </div>

      {/* Band 2 — left column (sidebar + referral) and right column
       *  (stat cards row + table). Flex row filling remaining height. */}
      <div className="flex min-h-0 flex-1">
        {/* Left column */}
        <div className="flex w-[min(140px,28%)] min-w-[108px] shrink-0 flex-col">
          <SidebarNav />
          <ReferralCard />
        </div>

        {/* Right column — stat cards row + agents table */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <div className="flex min-w-0 shrink-0 flex-col gap-2 px-2 sm:flex-row">
            {STAT_CARDS.map((card) => (
              <StatCard
                key={card.label}
                label={card.label}
                value={card.value}
                total={card.total}
                trend={card.trend}
                pos={card.pos}
              />
            ))}
          </div>

          <AgentsTable />
        </div>
      </div>
    </div>
  );
}
